import { TextField } from "@material-ui/core";
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogContentText from "@material-ui/core/DialogContentText";
import DialogTitle from "@material-ui/core/DialogTitle";
import { useDataProvider, useNotify } from "ra-core";
import React, { useEffect, useRef } from "react";
import { nowToAppLocalDateTime } from "../../../utils/date";
import { deliveryActions } from "../util";

interface Props {
  selectedIds?: number[];
  onComplete?: () => void;
}

const VerifyMultipleDeliveryDialog: React.FC<Props> = (props) => {
  const dataProvider = useDataProvider();
  const { selectedIds, onComplete } = props;
  const [open, setOpen] = React.useState(false);
  const [verificationNote, setVerificationNote] = React.useState("");
  const [isLoading, setIsLoading] = React.useState(false);
  const notify = useNotify();
  const verifyTime = nowToAppLocalDateTime();
  const isMountedRef = useRef(true);

  // Cleanup function to prevent memory leaks
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const handleClose = () => {
    setOpen(false);
    setVerificationNote("");
  };

  const handleAccept = async () => {
    if (!selectedIds || selectedIds.length === 0) {
      notify("No deliveries selected", "warning");
      return;
    }

    if (!isMountedRef.current) return;
    setIsLoading(true);

    try {
      // Verify each delivery individually
      const updatePromises = selectedIds.map((id) => {
        return dataProvider.update("deliveries", {
          id,
          data: {
            action: deliveryActions.VERIFY,
            verify_at: verifyTime,
            verification_note: verificationNote,
          },
          previousData: { id },
        });
      });

      await Promise.all(updatePromises);

      // Check if component is still mounted before updating state
      if (!isMountedRef.current) return;

      notify(`Successfully verified ${selectedIds.length} deliveries.`, "info");
      setOpen(false);
      setVerificationNote("");
      onComplete?.(); // Refresh and clear selection
    } catch (error: any) {
      if (isMountedRef.current) {
        notify(`Error: ${error.message}`, "warning");
      }
    } finally {
      if (isMountedRef.current) {
        setIsLoading(false);
      }
    }
  };

  const handleVerificationNoteChange = (
    event: React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    setVerificationNote(event.target.value);
  };

  const hasSelection = selectedIds && selectedIds.length > 0;

  const handleOpenDialog = async () => {
    if (!selectedIds || selectedIds.length === 0) {
      notify("Please select deliveries first", "warning");
      return;
    }

    setOpen(true);
  };

  return (
    <>
      <Button
        color="primary"
        onClick={handleOpenDialog}
        size={"small"}
        disabled={!hasSelection}
      >
        Verify Delivery {hasSelection ? `(${selectedIds.length})` : ""}
      </Button>

      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="form-dialog-title"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle id="form-dialog-title">
          Verify Multiple Deliveries
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            You have <strong>{selectedIds?.length || 0} deliveries</strong>{" "}
            selected.
            <br />
            <br />
            Please confirm that you have checked the POD (Proof of Delivery) for
            all selected deliveries and that all items have been correctly
            delivered.
          </DialogContentText>

          <TextField
            margin="dense"
            id="verification-note"
            label="Verification Note (Optional)"
            placeholder="Add any verification notes or observations for all selected deliveries..."
            multiline
            rows={4}
            fullWidth
            variant="outlined"
            value={verificationNote}
            onChange={handleVerificationNoteChange}
            style={{ marginTop: 16 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="secondary">
            Cancel
          </Button>
          <Button
            onClick={handleAccept}
            color="primary"
            disabled={!selectedIds?.length || isLoading}
          >
            {isLoading
              ? "Verifying..."
              : `Verify ${selectedIds?.length || 0} Deliveries`}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default VerifyMultipleDeliveryDialog;
